import { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react'
import type { ReactNode } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

type CheckResult = 'idle' | 'checking' | 'update-available' | 'up-to-date' | 'unsupported'

interface PWAUpdateValue {
  /** A new version is downloaded and waiting to be applied */
  needRefresh: boolean
  /** Apply the waiting update and reload */
  updateNow: () => void
  /** Manually poll the server for a newer service worker */
  checkForUpdate: () => Promise<void>
  /** State of the last manual check (for button feedback) */
  checkResult: CheckResult
}

const PWAUpdateContext = createContext<PWAUpdateValue | null>(null)

export function PWAUpdateProvider({ children }: { children: ReactNode }) {
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined)
  const [checkResult, setCheckResult] = useState<CheckResult>('idle')

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      registrationRef.current = registration ?? undefined
    },
  })

  // When a waiting update appears (background download finished), reflect it
  // in the manual-check state too, so a check that was "checking" resolves.
  useEffect(() => {
    if (needRefresh) setCheckResult('update-available')
  }, [needRefresh])

  // Foreground poll: when the installed app becomes visible, ask the SW to
  // check for a new version. This is what surfaces updates promptly on
  // mobile without the user having to do anything. Cheap — a conditional
  // GET that 304s when nothing changed.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        registrationRef.current?.update().catch(() => { /* offline / not ready */ })
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [])

  const updateNow = useCallback(() => {
    const reg = registrationRef.current
    const waiting = reg?.waiting

    // Reload as soon as the new worker takes control. We drive the reload
    // ourselves rather than relying on vite-plugin-pwa's auto-reload, which
    // hinges on an internal isUpdate flag that doesn't reliably fire — the
    // worker would activate but the page kept the old JS. controllerchange is
    // the deterministic signal that the new SW is now in charge.
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener(
        'controllerchange',
        () => window.location.reload(),
        { once: true },
      )
    }

    if (waiting) {
      // The generated SW listens for this and calls skipWaiting()
      waiting.postMessage({ type: 'SKIP_WAITING' })
    } else {
      // No tracked waiting worker (edge case) — let the plugin try, and
      // reload directly as a fallback so the user isn't left stuck.
      updateServiceWorker(false)
      window.location.reload()
    }
  }, [updateServiceWorker])

  const checkForUpdate = useCallback(async () => {
    const reg = registrationRef.current
    if (!reg) {
      // No SW (dev server, unsupported browser, or a non-installed context
      // where the SW hasn't registered yet)
      setCheckResult('unsupported')
      return
    }
    setCheckResult('checking')
    try {
      await reg.update()
      // After update(): a waiting/installing worker (or the hook already
      // flipping needRefresh) means a newer version exists.
      const hasUpdate = !!reg.waiting || !!reg.installing || needRefresh
      setCheckResult(hasUpdate ? 'update-available' : 'up-to-date')
    } catch {
      setCheckResult('up-to-date')
    }
  }, [needRefresh])

  return (
    <PWAUpdateContext.Provider value={{ needRefresh, updateNow, checkForUpdate, checkResult }}>
      {children}
    </PWAUpdateContext.Provider>
  )
}

export function usePWAUpdate(): PWAUpdateValue {
  const ctx = useContext(PWAUpdateContext)
  if (!ctx) {
    // Safe fallback if a consumer renders outside the provider (shouldn't
    // happen, but avoids a crash — the feature just no-ops).
    return {
      needRefresh: false,
      updateNow: () => {},
      checkForUpdate: async () => {},
      checkResult: 'unsupported',
    }
  }
  return ctx
}
